
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, ImageIcon } from "lucide-react";
import SimpleRichTextEditor from "@/components/blog/SimpleRichTextEditor";
import { Card, CardContent } from "@/components/ui/card";
import { sanitizeBlogContent } from "@/utils/sanitizer";

const EditBlog = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Development");
  const [published, setPublished] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textDirection, setTextDirection] = useState<"ltr" | "rtl">("ltr");
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  const categories = [
    "Development",
    "Design",
    "Product Management",
    "Freelancing",
    "Business",
    "Marketing",
  ];

  useEffect(() => {
    if (!id) return;
    
    const fetchBlogPost = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("id", id)
          .single();
          
        if (error) throw error;
        
        if (data) {
          setTitle(data.title);
          setContent(data.content);
          setCategory(data.category);
          setPublished(data.published || false);
          setImageUrl(data.image_url || "");
          
          // Type assertion to ensure text_direction is either "ltr" or "rtl"
          const direction = data.text_direction || "ltr";
          setTextDirection(direction === "rtl" ? "rtl" : "ltr");
          
          if (data.image_url) {
            setPreviewUrl(data.image_url);
          }
        }
      } catch (error: any) {
        console.error("Error fetching blog post:", error);
        toast({
          title: "Error",
          description: "Failed to load blog post: " + error.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBlogPost();
  }, [id, toast]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageFile(file);
      
      // Generate preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      
      // Upload image immediately
      handleImageUpload(file);
    }
  };
  
  const handleImageUpload = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      const { data, error } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);
        
      // Update the image URL
      setImageUrl(urlData.publicUrl);
      
      toast({
        title: "Image uploaded",
        description: "Your image has been uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: "Image upload failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const generateExcerpt = (content: string): string => {
    // Remove HTML tags
    const plainText = content.replace(/<[^>]+>/g, '');
    
    // Get first 150 characters as excerpt
    return plainText.substring(0, 150) + (plainText.length > 150 ? '...' : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (title.trim() === '' || content.trim() === '') {
      toast({
        title: "Missing content",
        description: "Please enter both title and content for your blog post.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      
      // Generate excerpt automatically from content
      const generatedExcerpt = generateExcerpt(content);
      
      const { error } = await supabase
        .from("blog_posts")
        .update({
          title,
          content,
          excerpt: generatedExcerpt,
          category,
          published,
          image_url: imageUrl,
          text_direction: textDirection,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
        
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Blog post updated successfully",
      });
      
      navigate("/blog");
    } catch (error: any) {
      console.error("Error updating blog post:", error);
      toast({
        title: "Error",
        description: "Failed to update blog post: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <Layout title="Edit Blog Post">
        <div className="max-w-4xl mx-auto px-4 py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout title="Edit Blog Post">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link to="/blog" className="inline-flex items-center text-sm mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blog
        </Link>
        
        <h1 className="text-3xl font-bold mb-8">Edit Blog Post</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter blog post title"
              required
              dir={textDirection}
              className={textDirection === "rtl" ? "text-right" : "text-left"}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="published" className="block mb-2">Published</Label>
              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={published}
                  onCheckedChange={setPublished}
                />
                <Label htmlFor="published">
                  {published ? "Published" : "Draft"}
                </Label>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Text Direction</Label>
            <Select value={textDirection} onValueChange={(value: "ltr" | "rtl") => setTextDirection(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select text direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ltr">Left to Right (LTR)</SelectItem>
                <SelectItem value="rtl">Right to Left (RTL)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500">Choose RTL for Arabic, Hebrew, or other right-to-left languages</p>
          </div>
          
          <div className="space-y-2">
            <Label>Featured Image</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  {previewUrl ? (
                    <div className="aspect-video relative">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gray-100 flex items-center justify-center text-gray-400">
                      <ImageIcon size={48} />
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <div className="flex flex-col justify-center">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  ref={imageInputRef}
                  className="mb-2"
                />
                <p className="text-xs text-gray-500">
                  Recommended size: 1200 x 630 pixels
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <SimpleRichTextEditor 
              value={content} 
              onChange={setContent}
              direction={textDirection}
            />
            <div className="mt-4">
              <div className="text-sm font-medium mb-2">Content Preview:</div>
              <div 
                className={`prose max-w-none p-4 border rounded-md ${textDirection === "rtl" ? "text-right" : "text-left"}`}
                dir={textDirection}
                dangerouslySetInnerHTML={{ __html: sanitizeBlogContent(content) }}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-4 pt-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/blog')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Update Blog Post"
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditBlog;
